import React, { ReactNode } from 'react';
import styled from 'styled-components';

import { CARD_TYPES } from '../../../shared/constants';
import { IContentfulLink, IImage, IParentPage } from '../../../types/shared';

import { ChevronRightLarge } from '../../atoms/icons/ChevronRightLarge';
import Link from '../../atoms/link/Link';

import { DEQ_TYPES } from '../../../utils/deq/deq.constants';
import { getInternalLink } from '../../../utils/generic/linkGenerator';

import mq from '../../../styles/breakpoints';
import Colors from '../../../styles/colors';
import { Body, H3 } from '../../../styles/generic';
import Spacings from '../../../styles/Spacings';

export interface TileProps {
  title: string;
  primaryLink: IContentfulLink;
  image?: IImage;
  children?: ReactNode;
  elementPosition?: number;
}

const Tile = ({
  title,
  image,
  children,
  primaryLink,
  elementPosition,
}: TileProps) => {
  const link = primaryLink?.linkedPage
    ? `/${getInternalLink(primaryLink?.linkedPage as IParentPage)}`
    : (primaryLink?.externalUrl as string);

  return (
    <StyledLink
      href={link}
      data-testid="tile"
      id={CARD_TYPES.tile}
      trackingComponent="theme-tile"
      trackingType={DEQ_TYPES.btnCard}
      trackingLabel={`${title}`.toLowerCase()}
      elementPosition={elementPosition}
    >
      {image && (
        <ImageContainer>
          <img data-testid="image" alt="" src={image.file?.url || image} />
        </ImageContainer>
      )}
      <TextContent>
        <StyledH3 data-testid="title">{title}</StyledH3>
        {children && <StyledBody data-testid="body">{children}</StyledBody>}
      </TextContent>
      <StyledIconContainer>
        <ChevronRightLarge color={Colors.abnGreen300} />
      </StyledIconContainer>
    </StyledLink>
  );
};

export default Tile;

const StyledIconContainer = styled.div`
  cursor: pointer;
  margin-left: ${Spacings.S};
  transition: transform 0.1s ease-in-out;
`;

const StyledLink = styled(Link)`
  align-items: center;
  background-color: ${Colors.white};
  border-radius: ${Spacings.XXS};
  box-shadow: ${({ theme }) => theme.elevations.abnPopover};
  box-sizing: border-box;
  display: flex;
  padding: ${Spacings.S};
  width: 100%;

  ${mq.from.breakpoint.M`
    padding: ${Spacings.M} ${Spacings.M} ${Spacings.M} ${Spacings.S};
  `}

  ${mq.from.breakpoint.L`
    width: calc(33.33% - ${Spacings.S});

    &:hover {
      background-color: ${Colors.abnGreen50};
      
    > ${StyledIconContainer} {
        transform: translateX(4px);
        transition: transform 0.1s ease-in-out;
    }
 `}

  &:active {
    background-color: ${Colors.abnGreen55};
    box-shadow: none;
  }
`;

const ImageContainer = styled.div`
  height: 72px;
  margin-right: ${Spacings.S};
  width: 72px;

  img {
    height: inherit;
    width: inherit;
  }
`;

const TextContent = styled.div`
  flex-grow: 1;
`;

const StyledH3 = styled(H3)`
  color: ${({ theme }) => theme.colors.abnGreen300};
  margin: 0;
  text-decoration: none;
`;

const StyledBody = styled(Body)`
  flex: 1;
  margin-top: 8px;
  text-decoration: none;
`;
